import { useRef, useState } from "react";
import api, { fmtErr, API } from "@/shared/lib/api";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CHUNK_SIZE = 8 * 1024 * 1024;       // 8 MB parts
const CHUNK_THRESHOLD = 6 * 1024 * 1024;  // chunk anything bigger than 6 MB

/**
 * FileUpload — accept file from disk, upload to backend, fill URL.
 * Large files (design / production artwork up to ~200 MB) are uploaded in
 * 8 MB chunks to bypass the gateway request-size limit, with a progress %.
 */
export default function FileUpload({ value, onChange, label = "Upload File", accept = ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx", testid, onBusyChange }) {
  const [busy, setBusyRaw] = useState(false);
  const [name, setName] = useState("");
  const [pct, setPct] = useState(0);
  const inputRef = useRef(null);

  function setBusy(b) { setBusyRaw(b); onBusyChange && onBusyChange(b); }

  function toFullUrl(url) {
    return url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;
  }

  async function uploadSmall(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => { if (e.total) setPct(Math.round((e.loaded / e.total) * 100)); },
    });
    return data;
  }

  async function uploadChunked(file) {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    let last = null;
    for (let i = 0; i < total; i++) {
      const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(total));
      fd.append("filename", file.name);
      fd.append("file", blob, file.name);
      const { data } = await api.post("/upload/chunk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      last = data;
      setPct(Math.round(((i + 1) / total) * 100));
    }
    return last;  // finalised payload from the last chunk
  }

  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setName(file.name);
    setPct(0);
    try {
      const data = file.size > CHUNK_THRESHOLD
        ? await uploadChunked(file)
        : await uploadSmall(file);
      onChange(toFullUrl(data.url));
      toast.success(`Uploaded ${data.filename}`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      setPct(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function clear() { onChange(""); setName(""); }

  return (
    <div className="space-y-1">
      {!value ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full h-11 px-3 border border-dashed border-zinc-400 hover:border-blue-600 hover:bg-blue-50 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          data-testid={testid}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? `Uploading ${name}… ${pct}%` : label}
        </button>
      ) : (
        <div className="flex items-center gap-2 border border-zinc-300 px-3 h-11 bg-zinc-50">
          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <a href={value} target="_blank" rel="noreferrer" className="flex-1 text-sm text-blue-600 hover:underline truncate font-mono">{value.split("/").pop()}</a>
          <button type="button" onClick={clear} className="text-zinc-500 hover:text-rose-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {busy && pct > 0 && (
        <div className="h-1 bg-zinc-100 rounded overflow-hidden" data-testid="upload-progress">
          <div className="h-full bg-blue-600 transition-all duration-200" style={{ width: `${pct}%` }} />
        </div>
      )}
      <input ref={inputRef} type="file" accept={accept} onChange={pick} className="hidden" />
    </div>
  );
}
